import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { vendorPortalApi, type DevicesParams } from '../../api/endpoints/vendor-portal';
import { useDebounce } from '../../hooks/useDebounce';
import { useVendorAuth } from '../../components/guards/RequireVendorAuth';
import { StatCard, Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { MetricBar } from '../../components/ui/MetricBar';
import { formatTimeAgo } from '../../utils/formatters';

type FilterType = 'all' | 'connected' | 'disconnected' | 'pending' | 'degraded' | 'critical';

export function VendorDashboardPage() {
  const navigate = useNavigate();
  const { vendor } = useVendorAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [refreshInterval, setRefreshInterval] = useState(30);

  const debouncedSearch = useDebounce(search);

  const devicesParams: DevicesParams = {
    page,
    limit: 20,
    search: debouncedSearch,
    ...(filter === 'connected' || filter === 'disconnected' || filter === 'pending' ? { status: filter } : {}),
    ...(filter === 'degraded' || filter === 'critical' ? { health: filter } : {}),
  };

  const { data: devicesData, refetch: refetchDevices } = useQuery({
    queryKey: ['vendor-devices', devicesParams],
    queryFn: () => vendorPortalApi.listDevices(devicesParams),
    refetchInterval: refreshInterval > 0 ? refreshInterval * 1000 : false,
  });

  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['vendor-stats'],
    queryFn: () => vendorPortalApi.getStats(),
    refetchInterval: refreshInterval > 0 ? refreshInterval * 1000 : false,
  });

  const { data: healthData, refetch: refetchHealth } = useQuery({
    queryKey: ['vendor-health'],
    queryFn: () => vendorPortalApi.getHealth(),
    refetchInterval: refreshInterval > 0 ? refreshInterval * 1000 : false,
  });

  const { data: activityData, refetch: refetchActivity } = useQuery({
    queryKey: ['vendor-activity'],
    queryFn: () => vendorPortalApi.getActivity(),
    refetchInterval: refreshInterval > 0 ? refreshInterval * 1000 : false,
  });

  const { data: alertsData, refetch: refetchAlerts } = useQuery({
    queryKey: ['vendor-alerts'],
    queryFn: () => vendorPortalApi.getAlerts(1, 10),
    refetchInterval: refreshInterval > 0 ? refreshInterval * 1000 : false,
  });

  const refreshAll = useCallback(() => {
    refetchDevices();
    refetchStats();
    refetchHealth();
    refetchActivity();
    refetchAlerts();
  }, [refetchDevices, refetchStats, refetchHealth, refetchActivity, refetchAlerts]);

  const devices = devicesData?.devices || [];
  const stats = statsData?.stats || { total: 0, connected: 0, disconnected: 0, pending: 0, error: 0 };
  const health = healthData?.health || { avg_cpu: 0, avg_memory: 0, avg_disk: 0, degraded: 0, critical: 0, total_with_metrics: 0 };
  const activity = activityData?.activity || [];
  const alerts = alertsData?.alerts || [];
  const totalPages = devicesData?.totalPages || 1;

  const handleFilter = (f: FilterType) => {
    setFilter(f);
    setPage(1);
  };

  const filterBtnClass = (f: FilterType) =>
    `px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
      filter === f ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
    }`;

  return (
    <div>
      {/* Header + Stats */}
      <div className="flex flex-col lg:flex-row mb-4 gap-4">
        <div className="lg:w-[30%]">
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Device overview for {vendor?.name || 'your organization'}</p>
        </div>
        <div className="lg:w-[70%] grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="cursor-pointer" onClick={() => handleFilter('all')}>
            <StatCard label="Total" value={stats.total} color={filter === 'all' ? 'teal' : 'gray'} />
          </div>
          <div className="cursor-pointer" onClick={() => handleFilter('connected')}>
            <StatCard label="Online" value={stats.connected} color="green" />
          </div>
          <div className="cursor-pointer" onClick={() => handleFilter('disconnected')}>
            <StatCard label="Offline" value={stats.disconnected} color="red" />
          </div>
          <div className="cursor-pointer" onClick={() => handleFilter('pending')}>
            <StatCard label="Pending" value={stats.pending + stats.error} color="yellow" />
          </div>
        </div>
      </div>

      {/* Main Row: Left (Health + Activity) / Right (Devices) */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        {/* Left Column */}
        <div className="lg:w-[30%] space-y-4">
          {/* Health Overview */}
          <Card title="Health Overview">
            <div className="p-4">
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Avg CPU</p>
                  <p className="text-lg font-bold text-gray-800 dark:text-white">{health.avg_cpu}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Avg Memory</p>
                  <p className="text-lg font-bold text-gray-800 dark:text-white">{health.avg_memory}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Avg Disk</p>
                  <p className="text-lg font-bold text-gray-800 dark:text-white">{health.avg_disk}%</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4">
                <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-yellow-500 mr-1" />
                  Degraded: <strong className="ml-1">{health.degraded}</strong>
                </span>
                <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-red-500 mr-1" />
                  Critical: <strong className="ml-1">{health.critical}</strong>
                </span>
              </div>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card title="Recent Activity">
            <div className="p-4 max-h-[300px] overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No recent activity</p>
              ) : (
                <div className="space-y-0">
                  {activity.map((a, i) => {
                    const borderColor = a.new_status === 'connected' ? 'border-green-500' : a.new_status === 'disconnected' ? 'border-red-500' : 'border-yellow-500';
                    const badgeVariant = a.new_status === 'connected' ? 'green' : a.new_status === 'disconnected' ? 'red' : 'yellow';
                    const label = a.new_status === 'connected' ? 'Online' : a.new_status === 'disconnected' ? 'Offline' : a.new_status;
                    return (
                      <div key={i} className={`flex items-start justify-between py-2 border-l-2 pl-3 ${borderColor}`}>
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="text-xs font-medium text-gray-800 dark:text-gray-200 break-words leading-snug">{a.device_name}</p>
                          <span className="text-xs text-gray-400">{formatTimeAgo(a.timestamp)}</span>
                        </div>
                        <Badge variant={badgeVariant} dot className="flex-shrink-0">
                          {label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Recent Alerts */}
          <Card title="Recent Alerts">
            <div className="p-4 max-h-[300px] overflow-y-auto">
              {alerts.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No recent alerts</p>
              ) : (
                <div className="space-y-0">
                  {alerts.map((alert, i) => {
                    const severityColor = alert.severity === 'critical' ? 'border-red-500' : 'border-yellow-500';
                    const severityVariant = alert.severity === 'critical' ? 'red' : 'yellow';
                    const metricLabels: Record<string, string> = {
                      cpu_percent: 'CPU',
                      memory_percent: 'Memory',
                      disk_percent: 'Disk',
                      api_error_rate: 'API Error Rate',
                      disconnect: 'Disconnect',
                    };
                    const metricLabel = metricLabels[alert.metric] || alert.metric;
                    const isDisconnect = alert.metric === 'disconnect';
                    const valueStr = isDisconnect ? `${Math.round(alert.value)} min` : `${alert.value}%`;
                    const thresholdStr = isDisconnect ? `${alert.threshold} min` : `${alert.threshold}%`;
                    return (
                      <div key={i} className={`py-2 border-l-2 pl-3 ${severityColor}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 mr-2">
                            <div className="flex items-center gap-1 mb-0.5">
                              <AlertTriangle size={10} className={alert.severity === 'critical' ? 'text-red-500' : 'text-yellow-500'} />
                              <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{alert.rule_name}</span>
                            </div>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 break-words">{alert.device_name}</p>
                            <p className="text-[10px] text-gray-400">
                              {metricLabel}: <span className="font-medium">{valueStr}</span> / {thresholdStr}
                            </p>
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0">
                            <Badge variant={severityVariant} className="mb-0.5">
                              {alert.severity}
                            </Badge>
                            <span className="text-[10px] text-gray-400">{formatTimeAgo(alert.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right Column: Device List */}
        <div className="lg:w-[70%]">
          <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
            {/* Device list header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Devices</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1.5 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      placeholder="Search..."
                      className="pl-7 pr-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-teal-500 dark:bg-gray-700 dark:text-white w-[180px]"
                    />
                  </div>
                  <select
                    value={refreshInterval}
                    onChange={e => setRefreshInterval(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-600 dark:text-gray-300 dark:bg-gray-700"
                  >
                    <option value={0}>Manual</option>
                    <option value={10}>10s</option>
                    <option value={30}>30s</option>
                    <option value={60}>1m</option>
                    <option value={120}>2m</option>
                    <option value={300}>5m</option>
                  </select>
                  <button onClick={refreshAll} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-xs font-medium flex items-center gap-1">
                    <RefreshCw size={12} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Filter pills */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => handleFilter('all')} className={filterBtnClass('all')}>All</button>
                <button onClick={() => handleFilter('connected')} className={filterBtnClass('connected')}>
                  <Wifi size={10} className="inline mr-0.5 -mt-px" />Online
                </button>
                <button onClick={() => handleFilter('disconnected')} className={filterBtnClass('disconnected')}>
                  <WifiOff size={10} className="inline mr-0.5 -mt-px" />Offline
                </button>
                <span className="text-gray-300 dark:text-gray-600 mx-0.5">|</span>
                <button onClick={() => handleFilter('degraded')} className={filterBtnClass('degraded')}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1" />Degraded
                </button>
                <button onClick={() => handleFilter('critical')} className={filterBtnClass('critical')}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />Critical
                </button>
              </div>
            </div>

            {/* Devices Table */}
            <Table>
              <Thead>
                <tr>
                  <Th>Device</Th>
                  <Th>Status</Th>
                  <Th>CPU</Th>
                  <Th>Memory</Th>
                  <Th>Disk</Th>
                  <Th>Last Seen</Th>
                  <Th>Actions</Th>
                </tr>
              </Thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {devices.map(device => {
                  const statusVariant = device.status === 'connected' ? 'green' : device.status === 'disconnected' ? 'red' : 'yellow';
                  const statusLabel = device.status === 'connected' ? 'Online' : device.status === 'disconnected' ? 'Offline' : device.status;
                  return (
                    <Tr key={device.id} onClick={() => navigate(`/vendor/devices/${device.id}`)}>
                      <Td>
                        <span className="font-medium text-gray-800 dark:text-gray-200" style={{ maxWidth: 180, wordBreak: 'break-word', display: 'inline-block' }}>
                          {device.name}
                        </span>
                      </Td>
                      <Td>
                        <Badge variant={statusVariant} dot>{statusLabel}</Badge>
                      </Td>
                      <Td><MetricBar value={device.metrics?.cpu_percent} color="blue" /></Td>
                      <Td><MetricBar value={device.metrics?.memory_percent} color="indigo" /></Td>
                      <Td><MetricBar value={device.metrics?.disk_percent} color="purple" /></Td>
                      <Td>{formatTimeAgo(device.last_seen || '')}</Td>
                      <Td>
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/vendor/devices/${device.id}`); }}
                          className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 text-xs font-medium"
                        >
                          View
                        </button>
                      </Td>
                    </Tr>
                  );
                })}
                {devices.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No devices found</td></tr>
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="px-4 pb-3">
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} color="teal" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
